import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { toLightweightDeviation } from '../utils/toLightweightDeviation';
import Component from './components/DeviationNodeView';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  deleteExtension,
  getExternalRenderer,
} from '../../components/utils/tiptap';
import { EXTENSION_TYPE_NAME } from './constants';
import { fetchFreshDeviations } from './puppy/fetchRefresh';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { elmoSaysThisMightBeADeviationUrl } from './helpers';
import { fetchDeviation } from './puppy/fetchDeviation';
import { Url } from '@wix/da-url';

export interface DaDeviationOptions {
  defaultWidth: number;
  maxWidth?: number;
  formMaxWidth?: number; // Used when we have a max that isn't the displayed max (dev description)
  minWidth?: number;
  smallMedia?: boolean;
  canLink?: boolean;
  canAlign?: boolean;
  canConfigure?: boolean;
  canResize?: boolean;
  defaultAlignment?: string;
  standaloneLink?: boolean;
}

export interface DaDeviationAttrs {
  alignment: 'left' | 'right' | 'center';
  link: string | null;
  width: number | null;
  deviation: PapiDeviation;
}

export enum MenuState {
  HIDDEN,
  SHOW,
}

export type DaDeviationStorage = {
  menuState: MenuState;
  deviationNode?: any;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    deviation: {
      daDeviationAddDeviation: (deviation: PapiDeviation) => ReturnType;
      daDeviationOpenSettingsMenu: () => ReturnType;
      daDeviationCloseSettingsMenu: () => ReturnType;
      daDeviationAlign: (alignment: 'left' | 'right' | 'center') => ReturnType;
      daDeviationLink: (href: string) => ReturnType;
      daDeviationReplace: (deviation: PapiDeviation) => ReturnType;
      daDeviationRefresh: () => ReturnType;
      daDeviationDelete: () => ReturnType;
      daDeviationToLink: () => ReturnType;
      daDeviationConvertToThumb: () => ReturnType;
    };
  }
}

export const DaDeviation = Node.create<DaDeviationOptions, DaDeviationStorage>({
  name: 'da-deviation',
  group: 'block unlinkable',
  inline: false,
  draggable: true,

  addStorage() {
    return {
      menuState: MenuState.HIDDEN,
    };
  },

  addOptions() {
    return {
      HTMLAttributes: {},
      defaultWidth: 780,
      smallMedia: false,
      canAlign: true,
      canLink: true,
      canConfigure: true,
      canResize: true,
    };
  },

  addAttributes() {
    return {
      deviation: {
        default: null,
      },
      alignment: {
        default: this.options.defaultAlignment ?? 'center',
      },
      link: {
        default: null,
      },
      width: {
        default: null,
      },
      // cropping???
    };
  },

  /**
   * Used to parse incoming html, usually via paste
   */
  parseHTML() {
    return [
      {
        tag: `figure[data-da-type="${EXTENSION_TYPE_NAME}"]`,
        getAttrs(node) {
          if (!node || !node.getAttribute) {
            return false;
          }

          const dataType = node.getAttribute('data-da-type');
          if (!dataType || dataType !== EXTENSION_TYPE_NAME) {
            return false;
          }
          try {
            return {
              deviation: JSON.parse(node.getAttribute('data-deviation') ?? ''),
              alignment: node.getAttribute('data-alignment') ?? undefined,
              link: node.getAttribute('data-link') ?? undefined,
              width: node.getAttribute('data-width') ?? undefined,
            };
          } catch (e) {}
          return null;
        },
      },
      {
        tag: `a[data-da-type="${EXTENSION_TYPE_NAME}"]`,
        getAttrs(node) {
          if (!node || !node.getAttribute) {
            return false;
          }
          const figure = node.querySelector(
            `figure[data-da-type="${EXTENSION_TYPE_NAME}"]`
          );
          if (!figure) {
            return false;
          }
          try {
            return {
              deviation: JSON.parse(
                figure.getAttribute('data-deviation') ?? ''
              ),
              alignment: figure.getAttribute('data-alignment') ?? undefined,
              link: figure.getAttribute('data-link') ?? undefined,
              width: figure.getAttribute('data-width') ?? undefined,
            };
          } catch (e) {}
          return null;
        },
      },
    ];
  },

  /**
   * Used to render HTML version of the doc, this is not used for viewer and not used
   * in edit mode either.
   *
   * Will be used for public API on the server
   *
   * This seems to be called for some reason on unmount and then everything is also
   * undefined so make sure you guard things.
   */
  renderHTML({ HTMLAttributes: { deviation, width, alignment, link } }) {
    const deviationMediaService = getExternalRenderer(
      this.editor,
      'mediaService'
    );

    if (!deviationMediaService) {
      return ['div'];
    }

    try {
      const img = [
        'img',
        mergeAttributes({
          alt: deviation.title,
          src: deviationMediaService.getLargestFullview(deviation).src,
          width,
          class: alignment,
        }),
      ];

      link = deviation.url;

      return [
        'figure',
        mergeAttributes({
          'data-da-type': EXTENSION_TYPE_NAME,
          'data-deviation': JSON.stringify(deviation),
          'data-width': width,
          'data-alignment': alignment,
          'data-link': link,
        }),
        link ? ['a', mergeAttributes({ href: link }), img] : img,
      ];
    } catch (e) {
      return ['span'];
    }
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component as any);
  },

  onSelectionUpdate() {
    if (this.editor.isActive('da-deviation')) {
      this.editor.commands.daDeviationOpenSettingsMenu();
    } else {
      this.editor.commands.daDeviationCloseSettingsMenu();
    }
  },

  addCommands() {
    return {
      daDeviationAddDeviation:
        deviation =>
        ({ commands, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.SHOW;
            return commands.insertContent({
              type: this.name,
              attrs: {
                deviation: toLightweightDeviation(deviation),
              },
            });
          }
          return true;
        },
      daDeviationOpenSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.SHOW;
          }
          return true;
        },
      daDeviationCloseSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.HIDDEN;
          }
          return true;
        },
      daDeviationAlign:
        alignment =>
        ({ editor, chain, dispatch, commands }) => {
          if (!this.options.canAlign) {
            return false;
          }
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              alignment,
            });
          }
          return true;
        },
      daDeviationLink:
        href =>
        ({ editor, chain, dispatch, commands }) => {
          if (!this.options.canLink) {
            return false;
          }
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              link: href,
            });
          }
          return true;
        },
      daDeviationDelete:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (deleteExtension(EXTENSION_TYPE_NAME, editor, dispatch, state)) {
            this.storage.menuState = MenuState.HIDDEN;
            return true;
          }
          return true;
        },
      daDeviationRefresh:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            const { selection } = state;
            const { from, to } = selection;
            let attributes: any = null;

            state.doc.nodesBetween(from, to, node => {
              if (node.type.name === this.name) {
                attributes = node.attrs;
              }
            });

            if (attributes) {
              fetchFreshDeviations({
                deviations: [attributes.deviation],
                editor,
              })
                .then(result => {
                  if (result.error) {
                    alert(result.error);
                  } else {
                    editor.chain().focus().daDeviationReplace(result[0]).run();
                  }
                })
                .catch(e => {
                  alert(e.message);
                });
            }

            return true;
          }
          return true;
        },
      daDeviationReplace:
        deviation =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            commands.updateAttributes(this.name, {
              deviation: toLightweightDeviation(deviation),
            });
            return true;
          }
          return true;
        },
      daDeviationConvertToThumb:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            const { selection } = state;
            const { from, to } = selection;
            let attributes: any = null;

            state.doc.nodesBetween(from, to, node => {
              if (node.type.name === this.name) {
                attributes = node.attrs;
              }
            });

            if (attributes) {
              return chain()
                .daDeviationDelete()
                .daDeviationThumbAddDeviation(attributes.deviation)
                .daDeviationThumbLink(attributes.link ?? '')
                .run();
            }
            return true;
          }
          return true;
        },
      daDeviationToLink:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (dispatch) {
            const { selection } = state;
            const { from, to } = selection;
            let attributes: any = null;

            state.doc.nodesBetween(from, to, node => {
              if (node.type.name === this.name) {
                attributes = node.attrs;
              }
            });

            if (attributes && attributes.deviation) {
              const deviation: PapiDeviation = attributes.deviation;
              let link = '';
              if (deviation.isPublished) {
                link = deviation.url ?? deviation.shortUrl ?? '';
              } else {
                link = Url.stashItemLink(
                  deviation.stashPrivateid?.toString(36) ?? ''
                );
              }
              chain()
                .daDeviationDelete()
                .insertContent({
                  type: 'text',
                  marks: [
                    {
                      type: 'link',
                      attrs: {
                        href: link,
                      },
                    },
                  ],
                  text: deviation.title ?? link,
                })
                .run();
            }
            return true;
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('da-deviation-paste'),
        props: {
          handlePaste: (view, event, slice) => {
            const text = event.clipboardData?.getData('text/plain') ?? '';

            if (!/^(https?:\/\/|:thumb)/.test(text)) {
              return false;
            }
            const matchedDeviation = elmoSaysThisMightBeADeviationUrl(text);

            if (matchedDeviation) {
              this.editor.setEditable(false);
              fetchDeviation(this.editor, matchedDeviation)
                .then(result => {
                  this.editor.setEditable(true);
                  if (result.error) {
                    this.editor
                      .chain()
                      .insertContent({
                        type: 'text',
                        marks: [
                          {
                            type: 'link',
                            attrs: {
                              href: text,
                            },
                          },
                        ],
                        text: text,
                      })
                      .run();
                  } else {
                    this.editor
                      .chain()
                      .focus()
                      .daDeviationAddDeviation(result)
                      .run();
                  }
                })
                .catch(e => {
                  this.editor.setEditable(true);
                  this.editor
                    .chain()
                    .insertContent({
                      type: 'text',
                      marks: [
                        {
                          type: 'link',
                          attrs: {
                            href: text,
                          },
                        },
                      ],
                      text: text,
                    })
                    .run();
                });

              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
